// UI ヘルパー
// XSS 対策: 動的データを HTML に挿入する際は必ず escapeHtml を通す (CLAUDE.md S3-4)

export function el(html) {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstChild;
}

export function escapeHtml(str) {
  if (str === null || str === undefined) return '';
  const div = document.createElement('div');
  div.textContent = String(str);
  return div.innerHTML;
}

// マークダウンインライン要素を最小限変換する（リンクのみ）
// `[text](url)` → `<a href="url" target="_blank" rel="noopener noreferrer">text</a>`
// http/https のみ許可、URL とテキスト両方を escapeHtml で挟む（XSS 防止）
function renderInlineLinks(s) {
  if (!s) return '';
  const out = [];
  let last = 0;
  const re = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
  let m;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) out.push(escapeHtml(s.slice(last, m.index)));
    const linkText = escapeHtml(m[1]);
    const linkUrl = escapeHtml(m[2]);
    out.push(`<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`);
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push(escapeHtml(s.slice(last)));
  return out.join('');
}

// 改行を保つ簡易マークダウン（##/### + 段落 + インラインリンク）
export function renderLongText(text) {
  if (!text) return '';
  const lines = String(text).split('\n');
  const out = [];
  let buf = [];
  const flush = () => {
    const p = buf.join(' ').trim();
    if (p) out.push(`<p>${renderInlineLinks(p)}</p>`);
    buf = [];
  };
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flush();
    } else if (trimmed.startsWith('### ')) {
      flush();
      out.push(`<h3>${escapeHtml(trimmed.slice(4))}</h3>`);
    } else if (trimmed.startsWith('## ')) {
      flush();
      out.push(`<h2>${escapeHtml(trimmed.slice(3))}</h2>`);
    } else if (trimmed.startsWith('# ')) {
      flush();
      out.push(`<h2>${escapeHtml(trimmed.slice(2))}</h2>`);
    } else {
      buf.push(trimmed);
    }
  }
  flush();
  return out.join('\n');
}

// 画面遷移
const SCREEN_ROOT = () => document.getElementById('screen-mount');
const HOME_ROOT = () => document.getElementById('screen-home');
let _backStack = ['home'];

export function showHome() {
  HOME_ROOT().classList.add('screen--active');
  SCREEN_ROOT().innerHTML = '';
  _backStack = ['home'];
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

export function showScreen(name, content) {
  HOME_ROOT().classList.remove('screen--active');
  const mount = SCREEN_ROOT();
  mount.innerHTML = '';
  const screen = document.createElement('main');
  screen.className = 'screen screen--active';
  screen.id = `screen-${name}`;
  screen.innerHTML = content;
  mount.appendChild(screen);
  if (_backStack[_backStack.length - 1] !== name) _backStack.push(name);
  window.scrollTo({ top: 0, behavior: 'instant' });
  return screen;
}

export function backToHome() {
  showHome();
}

// 戻るボタン共通HTML
export function screenHead(title, opts = {}) {
  const right = opts.right || '';
  return `
    <header class="screen-head">
      <button class="btn btn--icon" data-action="back" type="button" aria-label="戻る">
        <svg class="icon"><use href="#i-back"/></svg>
      </button>
      <h1 class="screen-head__title">${escapeHtml(title)}</h1>
      <div class="screen-head__action">${right}</div>
    </header>
  `;
}

// Toast
let toastTimer = null;
export function toast(message, ms = 2400) {
  const t = document.getElementById('toast');
  t.textContent = message;
  t.hidden = false;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    t.hidden = true;
  }, ms);
}

// Overlay (loading)
export function showLoading(message = '処理中…') {
  const ov = document.getElementById('overlay');
  document.getElementById('overlay-msg').textContent = message;
  ov.hidden = false;
}

export function hideLoading() {
  document.getElementById('overlay').hidden = true;
}

// 検索 / フィルタ用ユーティリティ
export function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // 日本時間で表示（Notion の date は通常 YYYY-MM-DD のため日本での表示と一致させる）
  return d
    .toLocaleDateString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    .replace(/\//g, '.');
}

export function fmtRelative(ts) {
  if (!ts) return '未同期';
  const diff = Date.now() - ts;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}秒前に同期`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}分前に同期`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}時間前に同期`;
  const day = Math.floor(hr / 24);
  return `${day}日前に同期`;
}

// AI 履歴の共通描画ヘルパー
// items を「過去の実行履歴」セクションとして HTML 化
// items が空でも「まだ実行履歴がありません」を表示する（ユーザー UI で履歴の有無が明確になるよう）
export function renderHistorySection(items) {
  const hasItems = items && items.length > 0;
  const body = hasItems
    ? `<ul class="hist-list">${items
        .map((it) => {
          const dateStr = new Date(it.createdAt).toLocaleString('ja-JP', {
            timeZone: 'Asia/Tokyo',
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });
          return `
            <li class="hist-row" data-hist-id="${escapeHtml(it.id)}">
              <div style="flex:1;min-width:0;">
                <p class="hist-row__meta">${escapeHtml(dateStr)}</p>
                <p class="hist-row__title">${escapeHtml(it.title || '')}</p>
                ${it.subtitle ? `<p class="hist-row__sub">${escapeHtml(it.subtitle)}</p>` : ''}
              </div>
              <button class="btn btn--icon hist-row__del" data-hist-delete="${escapeHtml(it.id)}" type="button" aria-label="削除">
                <svg class="icon" style="width:12px;height:12px;color:var(--mute);"><use href="#i-close"/></svg>
              </button>
            </li>
          `;
        })
        .join('')}</ul>`
    : `<p style="font-size:13px;color:var(--mute);text-align:center;padding:24px 0;font-style:italic;">まだ実行履歴はありません</p>`;
  return `
    <p class="section-label" style="margin-top:32px;">past results</p>
    ${body}
  `;
}

// ============ 読書圏フィルター ============
// 各 AI 機能の入力フォームで「対象書籍の絞り込み」を共通 UI で提供する

const GENRES = ['哲学', 'ビジネス', '歴史', 'ファッション', 'アート', '小説', 'エッセイ', '写真'];

// フィルター UI を HTML 化（開閉可能なディテール形式）
export function renderBookFilter() {
  return `
    <details class="filter-block" id="book-filter">
      <summary class="filter-block__sum">
        <span><svg class="icon" style="width:14px;height:14px;margin-right:6px;"><use href="#i-filter"/></svg>対象書籍を絞り込む</span>
        <span class="filter-block__count" id="filter-count"></span>
      </summary>
      <div class="filter-block__body">
        <div class="form-group">
          <label class="form-label">期間</label>
          <select class="form-select" data-filter="period">
            <option value="all">全期間</option>
            <option value="30">直近 30 日</option>
            <option value="90">直近 90 日</option>
            <option value="thisyear">今年</option>
            <option value="lastyear">昨年</option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">ジャンル</label>
          <select class="form-select" data-filter="genre">
            <option value="all">すべて</option>
            ${GENRES.map((g) => `<option value="${escapeHtml(g)}">${escapeHtml(g)}</option>`).join('')}
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">ステータス</label>
          <select class="form-select" data-filter="status">
            <option value="all">すべて</option>
            <option value="読了">読了のみ</option>
            <option value="読書中">読書中のみ</option>
            <option value="積読">積読のみ</option>
          </select>
        </div>
      </div>
    </details>
  `;
}

// 現在のフィルター UI の状態を読み取る
export function getFilterState(scope) {
  const root = scope || document;
  const get = (k) => root.querySelector(`[data-filter="${k}"]`)?.value || 'all';
  return {
    period: get('period'),
    genre: get('genre'),
    status: get('status'),
  };
}

// フィルター適用
export function applyBookFilter(books, state) {
  if (!state) return books;
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;
  return books.filter((b) => {
    // 期間
    if (state.period && state.period !== 'all') {
      const dateStr = b.endDate || b.startDate;
      if (!dateStr) return false;
      const t = new Date(dateStr).getTime();
      if (Number.isNaN(t)) return false;
      if (state.period === '30' && now - t > 30 * day) return false;
      if (state.period === '90' && now - t > 90 * day) return false;
      if (state.period === 'thisyear') {
        const y = new Date().getFullYear();
        if (new Date(dateStr).getFullYear() !== y) return false;
      }
      if (state.period === 'lastyear') {
        const y = new Date().getFullYear() - 1;
        if (new Date(dateStr).getFullYear() !== y) return false;
      }
    }
    // ジャンル
    if (state.genre && state.genre !== 'all') {
      if (!(b.genre || []).includes(state.genre)) return false;
    }
    // ステータス
    if (state.status && state.status !== 'all') {
      if (b.status !== state.status) return false;
    }
    return true;
  });
}

// フィルター変更時に件数を更新するための bind
export function bindFilterCounter(screen, books) {
  const update = () => {
    const state = getFilterState(screen);
    const filtered = applyBookFilter(books, state);
    const out = screen.querySelector('#filter-count');
    if (out) {
      const total = books.length;
      const allSelected = state.period === 'all' && state.genre === 'all' && state.status === 'all';
      out.textContent = allSelected ? `${total} 冊` : `${filtered.length} / ${total} 冊`;
    }
  };
  update();
  screen.querySelectorAll('[data-filter]').forEach((el) => el.addEventListener('change', update));
}

// 履歴行のクリック/削除イベント共通バインド
export async function bindHistoryHandlers(screen, items, replayFn, redrawFn) {
  const { deleteAiHistory } = await import('./cache.js');
  screen.querySelectorAll('[data-hist-id]').forEach((row) => {
    const id = parseInt(row.dataset.histId, 10);
    row.addEventListener('click', (e) => {
      if (e.target.closest('[data-hist-delete]')) return;
      const entry = items.find((x) => x.id === id);
      if (entry) replayFn(entry);
    });
    row.querySelector('[data-hist-delete]')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (!confirm('この履歴を削除しますか？')) return;
      await deleteAiHistory(id);
      toast('削除しました');
      if (redrawFn) await redrawFn();
    });
  });
}

export function ratingToInt(rating) {
  if (!rating) return 0;
  return [...rating].filter((c) => c === '★').length;
}

// 検索/Amazon・古本屋リンク生成
export function amazonSearchUrl(title, author) {
  return `https://www.amazon.co.jp/s?k=${encodeURIComponent((title || '') + ' ' + (author || ''))}`;
}
export function koshoSearchUrl(title, author) {
  return `https://www.kosho.or.jp/products/list.php?search_word=${encodeURIComponent((title || '') + ' ' + (author || ''))}`;
}

// 書籍提案カード（recommend / keyword-search で共用）
// item: { title, author, genre, summary, learnings[], reason, connection, level, rarity }
// opts: { onAdd, isInWishlist, onRemove }
export function renderSuggestionCard(item, opts = {}) {
  const isIn = !!opts.isInWishlist;
  const learnings = (item.learnings || [])
    .map((l) => `<li>${escapeHtml(l)}</li>`)
    .join('');
  const meta = [item.author, item.genre, item.level, item.rarity]
    .filter(Boolean)
    .map(escapeHtml)
    .join(' · ');
  return `
    <article class="result-card" data-suggestion-id="${escapeHtml(opts.id || '')}">
      <h3 class="result-card__title">${escapeHtml(item.title)}</h3>
      ${meta ? `<p class="result-card__meta">${meta}</p>` : ''}
      ${item.summary ? `<p class="result-card__body" style="margin-top:8px;"><strong>概要:</strong> ${escapeHtml(item.summary)}</p>` : ''}
      ${learnings ? `<div style="margin-top:10px;"><p class="result-card__body" style="margin-bottom:4px;"><strong>学べること:</strong></p><ul style="margin:0;padding-left:18px;font-size:13px;color:var(--dim);line-height:1.7;">${learnings}</ul></div>` : ''}
      ${item.reason ? `<p class="result-card__body" style="margin-top:10px;"><strong>あなたへの推薦理由:</strong> ${escapeHtml(item.reason)}</p>` : ''}
      ${item.connection ? `<p class="result-card__body" style="margin-top:6px;font-style:italic;color:var(--mute);">→ ${escapeHtml(item.connection)}</p>` : ''}
      <div class="result-card__chips" style="margin-top:14px;">
        <a class="chip" href="${escapeHtml(amazonSearchUrl(item.title, item.author))}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;">Amazon で探す</a>
        <a class="chip" href="${escapeHtml(koshoSearchUrl(item.title, item.author))}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;">日本の古本屋</a>
      </div>
      <button class="btn btn--small btn--block ${isIn ? '' : 'btn--ink'}" data-wishlist-toggle type="button" style="margin-top:12px;">
        <svg class="icon"><use href="#${isIn ? 'i-check' : 'i-bookmark'}"/></svg>
        <span>${isIn ? '購入予定リストに追加済み（解除する）' : '購入予定リストに加える'}</span>
      </button>
    </article>
  `;
}
